import apiClient from "./api-client";

const multipartApiClient = apiClient({ baseURL: "/" });
const LOCAL_STORAGE_KEY = "multipart_upload_state";

/**
 * Check chunk size optimal based on network speed
 * @returns {number} - Suitable chunk size (byte)
 */
const getOptimalChunkSize = () => {
  // Default chunk size is 10MB
  const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024;

  // Try to get connection information from Navigator API
  if (navigator.connection) {
    const { effectiveType, downlink } = navigator.connection;

    // Adjust chunk size based on network conditions
    if (effectiveType === "4g" && downlink > 5) {
      return 50 * 1024 * 1024; // 50MB for good connection
    } else if (effectiveType === "4g" || effectiveType === "3g") {
      return 10 * 1024 * 1024; // 10MB for average connection
    } else {
      return 5 * 1024 * 1024; // 5MB for slow connection
    }
  }

  return DEFAULT_CHUNK_SIZE;
};

/**
 * Initialize multipart upload on S3
 * @param {File} file - File to upload
 * @returns {Promise<{upload_id: string, key: string}>} - Upload ID and key
 */
export const initMultipartUpload = async (file) => {
  try {
    // Abort any existing uploads for this file
    const savedState = getUploadState(file.name);
    if (savedState) {
      await abortMultipartUpload(savedState.upload_id, savedState.key);
      clearUploadState(file.name);
    }

    const response = await multipartApiClient.post(
      "/akaire_feature/multipart_uploads/initiate",
      {
        objectName: file.name,
        content_type: file.type,
        size: file.size,
      }
    );

    // Save initial state
    saveUploadState(file.name, {
      upload_id: response.data.upload_id,
      key: response.data.key,
      file_size: file.size,
      parts_completed: [],
      started_at: new Date().toISOString(),
    });

    return response.data;
  } catch (error) {
    console.error("Error initializing multipart upload:", error);
    throw error;
  }
};

/**
 * Save upload state to resume when browser refresh
 */
const saveUploadState = (fileName, state) => {
  try {
    const currentState = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    currentState[fileName] = state;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentState));
  } catch (e) {
    console.warn("Failed to save upload state:", e);
  }
};

/**
 * Get saved upload state
 */
export const getUploadState = (fileName) => {
  try {
    const currentState = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    return currentState[fileName] || null;
  } catch (e) {
    console.warn("Failed to get upload state:", e);
    return null;
  }
};

/**
 * Clear saved upload state
 */
const clearUploadState = (fileName) => {
  try {
    const currentState = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    delete currentState[fileName];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentState));
  } catch (e) {
    console.warn("Failed to clear upload state:", e);
  }
};

/**
 * Abort multipart upload
 * @param {string} uploadId - Upload ID
 * @param {string} key - S3 key
 */
export const abortMultipartUpload = async (uploadId, key) => {
  try {
    await multipartApiClient.post("/akaire_feature/multipart_uploads/abort", {
      upload_id: uploadId,
      key: key,
    });
    console.log("Multipart upload aborted successfully");
  } catch (error) {
    console.error("Error aborting multipart upload:", error);
  }
};

/**
 * Upload parallel parts of file with retry and save state
 * @param {File} file - File to upload
 * @param {string} uploadId - Upload ID from S3
 * @param {string} key - S3 key
 * @param {number} concurrentUploads - Number of concurrent uploads
 * @param {Function} onProgressCallback - Callback when there is upload progress
 * @returns {Promise<Array>} - List of uploaded parts
 */
export const uploadInParallel = async (
  file,
  uploadId,
  key,
  concurrentUploads = 3,
  onProgressCallback = null
) => {
  const chunkSize = getOptimalChunkSize();
  const totalParts = Math.ceil(file.size / chunkSize);
  const partNumbers = [...Array(totalParts).keys()].map((i) => i + 1);

  // Initialize result array with enough elements
  const uploadedParts = new Array(totalParts).fill(null);

  // Get saved state (if any)
  const savedState = getUploadState(file.name);
  let completedParts = [];
  let simulatedProgress = 0;
  let intervalId = null;

  if (savedState && savedState.upload_id === uploadId) {
    completedParts = savedState.parts_completed || [];

    // Resume completed parts
    completedParts.forEach((part) => {
      uploadedParts[part.PartNumber - 1] = part;
    });

    // Update progress if there is callback
    if (onProgressCallback) {
      if (completedParts.length === 0) {
        intervalId = setInterval(() => {
          simulatedProgress += 1;
          onProgressCallback(simulatedProgress);
          // Stop simulated progress at 99 to prevent exceeding 100%
          // and ensure `isBackToRealProgress` condition can eventually be satisfied.
          if (simulatedProgress >= 85) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }, Math.max(totalParts * 5, 2000));
      } else {
        if (intervalId) clearInterval(intervalId);
        const progress = Math.round((completedParts.length / totalParts) * 100);
        onProgressCallback(progress);
      }
    }
  }

  // Filter out parts that are already uploaded
  const remainingParts = partNumbers.filter(
    (partNumber) =>
      !completedParts.some((part) => part.PartNumber === partNumber)
  );

  if (remainingParts.length === 0) {
    console.log("All parts already uploaded, resuming from saved state");
    return uploadedParts.filter(Boolean); // Filter out null values
  }

  // Create partsData for remaining parts
  const partsData = remainingParts.map((partNumber) => {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    return {
      partNumber,
      blob: file.slice(start, end),
    };
  });

  // Counter for completed parts
  let completed = completedParts.length;
  const MAX_RETRIES = 1;

  const uploadSinglePart = async ({ partNumber, blob }, retryCount = 0) => {
    try {
      const presignUrlRes = await multipartApiClient.get(
        `/akaire_feature/multipart_uploads/presign_part?upload_id=${uploadId}&key=${encodeURIComponent(
          key
        )}&part_number=${partNumber}`
      );
      const { url } = presignUrlRes.data;

      // Use Promise with XHR to track progress better
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);

        // Add timeout to avoid request hanging
        xhr.timeout = 180000; // 3 minutes timeout

        xhr.onload = () => {
          if (xhr.status === 200) {
            const rawEtag = xhr.getResponseHeader("ETag");

            if (!rawEtag) {
              reject(
                new Error(
                  "ETag header is missing — check S3 CORS configuration"
                )
              );
              return;
            }

            const etag = rawEtag.replace(/"/g, "");
            const partData = { ETag: etag, PartNumber: partNumber };

            // Update array at the correct position
            uploadedParts[partNumber - 1] = partData;

            // Count completed parts
            completed++;

            // Update saved state
            const state = getUploadState(file.name);
            if (state) {
              state.parts_completed = uploadedParts.filter(Boolean);
              saveUploadState(file.name, state);
            }

            // Update progress
            if (onProgressCallback) {
              const progress = Math.round((completed / totalParts) * 100);
              const isBackToRealProgress = progress >= simulatedProgress;
              if ((simulatedProgress >= 85 || isBackToRealProgress) && intervalId) {
                clearInterval(intervalId);
              }

              if (isBackToRealProgress) {
                onProgressCallback(progress);
              }
            }

            resolve(partData);
          } else {
            reject(
              new Error(`Failed to upload part ${partNumber}: ${xhr.status}`)
            );
          }
        };

        xhr.onerror = () =>
          reject(new Error(`Network error for part ${partNumber}`));
        xhr.ontimeout = () =>
          reject(new Error(`Timeout uploading part ${partNumber}`));
        xhr.send(blob);
      });
    } catch (error) {
      // Retry logic
      if (retryCount < MAX_RETRIES) {
        console.warn(
          `Retrying upload for part ${partNumber}, attempt ${retryCount + 1}`
        );
        // Exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, retryCount) * 1000)
        );
        return uploadSinglePart({ partNumber, blob }, retryCount + 1);
      }

      console.error(
        `Failed to upload part ${partNumber} after ${MAX_RETRIES} retries:`,
        error
      );
      throw error;
    }
  };

  // Process in parallel with semaphore pattern to control better
  const semaphore = {
    count: concurrentUploads,
    queue: [],

    async acquire() {
      if (this.count > 0) {
        this.count--;
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        this.queue.push(resolve);
      });
    },

    release() {
      if (this.queue.length > 0) {
        const resolve = this.queue.shift();
        resolve();
      } else {
        this.count++;
      }
    },
  };

  try {
    // Perform upload of remaining parts with semaphore
    await Promise.all(
      partsData.map(async (part) => {
        await semaphore.acquire();
        try {
          await uploadSinglePart(part);
        } finally {
          semaphore.release();
        }
      })
    );

    // Filter out only non-null elements
    return uploadedParts.filter(Boolean);
  } catch (error) {
    // Abort upload and throw error
    console.error("Error during parallel upload:", error);
    await abortMultipartUpload(uploadId, key);
    throw error;
  }
};

/**
 * Complete multipart upload
 * @param {string} uploadId - Upload ID from S3
 * @param {Array} parts - List of parts
 * @param {string} key - S3 key
 * @returns {Promise<{url: string}>} - URL of uploaded file
 */
export const completeMultipartUpload = async (uploadId, parts, key) => {
  try {
    const response = await multipartApiClient.post(
      "/akaire_feature/multipart_uploads/complete",
      {
        upload_id: uploadId,
        parts: parts,
        key: key,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error completing multipart upload:", error);
    throw error;
  }
};

/**
 * Utility function to perform the entire multipart upload process
 * @param {File} file - File to upload
 * @param {Function} onProgressCallback - Callback when there is upload progress
 * @param {number} concurrentUploads - Number of concurrent uploads
 * @returns {Promise<{url: string}>} - URL of uploaded file
 */
export const performMultipartUpload = async (
  file,
  onProgressCallback = null,
  concurrentUploads = 3
) => {
  try {
    // Check if there is saved upload state
    const savedState = getUploadState(file.name);
    let upload_id, key;

    if (savedState && savedState.file_size === file.size) {
      // Abort any existing upload instead of resuming
      await abortMultipartUpload(savedState.upload_id, savedState.key);
      clearUploadState(file.name);
      console.log("Found existing upload state, aborting previous upload");

      // Initialize new upload
      const initResult = await initMultipartUpload(file);
      upload_id = initResult.upload_id;
      key = initResult.key;
    } else {
      // Initialize new upload
      const initResult = await initMultipartUpload(file);
      upload_id = initResult.upload_id;
      key = initResult.key;
    }

    // Upload parts
    if (concurrentUploads == 1) {
      await sleep(5000);
    }

    const parts = await uploadInParallel(
      file,
      upload_id,
      key,
      concurrentUploads,
      onProgressCallback
    );

    // Complete multipart upload
    const result = await completeMultipartUpload(upload_id, parts, key);

    // Clear saved state after successful upload
    clearUploadState(file.name);

    return result;
  } catch (error) {
    console.error("Error during multipart upload process:", error);
    throw error;
  }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export default {
  initMultipartUpload,
  uploadInParallel,
  completeMultipartUpload,
  performMultipartUpload,
  abortMultipartUpload,
  getUploadState,
};
